import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, EMPTY } from "rxjs";
import { catchError, mapTo } from "rxjs/operators";
import { ConfigService } from "../../../../lib/config/config.service";
import { MessageService } from "../../../message/message.service";
import {
  IdVerseStateData,
  UpdateIdentityVerificationPayload,
  StartNewIdVerseResponse,
  StoreVerificationData,
} from "../domain/models/identity-verification.model";
import { IdentityVerificationInfo } from "../domain/models/identity-verification.model";

@Injectable({
  providedIn: "root",
})
export class IdentityVerificationRemote {
  private readonly baseUrl: string;
  private readonly idVerseUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    private readonly messageService: MessageService
  ) { 
    this.baseUrl = `${this.configService.environment.customersAPI.url}/identity-verification`;
    this.idVerseUrl = `${this.configService.environment.customersAPI.url}/idverse`;
  }

  storeVerification(customerId: number): Observable<StoreVerificationData> {
    return this.http
      .post<StoreVerificationData>(`${this.idVerseUrl}/store-verification`, { customerId })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  refresh(transactionId: string, customerId: number): Observable<void> {
    return this.http
      .post<void>(`${this.idVerseUrl}/refresh`, { transactionId, customerId })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  invalidate(transactionId: string, customerId: number): Observable<void> {
    return this.http
      .post<void>(`${this.idVerseUrl}/cancel-verification`, {
        transactionId,
        customerId,
      })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  confirmSwitchToKba(): Observable<void> {
    return this.http
      .post<void>(`${this.idVerseUrl}/confirm-switch-to-kba`, null)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  mobileFlowCompleted(): Observable<boolean> {
    return this.http
      .post<void>(`${this.idVerseUrl}/mobile-flow-completed`, null)
      .pipe(
        mapTo(true),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  getIdVerseState(hideLoader: boolean): Observable<IdVerseStateData> {

    let headers = new HttpHeaders(this.configService.environment.cache.noCache)
      .set('Cache-Control', 'no-cache, no-store, max-age=0')
      .set('Pragma', 'no-cache');
    if (hideLoader) {
      headers = headers.set('hideLoader', 'true');
    }

    return this.http.get<IdVerseStateData>(`${this.idVerseUrl}/get-state`, { headers })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  getInfo(): Observable<IdentityVerificationInfo> {
    return this.http
      .get<IdentityVerificationInfo>(`${this.idVerseUrl}/info`)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  updateIdentityVerification(
    payload: UpdateIdentityVerificationPayload,
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}`, payload)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  startNewIdVerse(customerId: number): Observable<StartNewIdVerseResponse> {
    return this.http
      .post<StartNewIdVerseResponse>(`${this.baseUrl}/start-new-idverse`, { customerId })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  startNewKba(customerId: number): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/start-new-kba`, { customerId })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  setVerified(customerId: number): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/set-verified`, { customerId })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    switch (error.status) {
      case 400:
      case 404:
      case 422:
        this.messageService.error(error);
        break;
      case 401:
        console.warn("Unauthorized access - should be handled by interceptor");
        break;
      case 500:
        this.messageService.error(
          "Internal server error occurred. Please try again later.",
        );
        break;
      default:
        this.messageService.error(
          "An unexpected error occurred. Please try again later.",
        );
        break;
    }

    return EMPTY;
  }
}
